import * as React from "react"
import { cn } from "@/lib/utils"

export type DocsHeading = {
  id: string
  text: string
  level: number
}

export type DocsSection = {
  id: string
  title: string
  headings: DocsHeading[]
}

interface DocsSidebarNavProps {
  sections: DocsSection[]
  onNavigate?: () => void
}

// Sidebar nav for /docs/api. One top-level entry per markdown file
// (Overview, Authentication, Pieces, …); h2 anchors inside the file
// render as nested links. IntersectionObserver scroll-spy highlights
// whichever h2 is currently in view; if no h2 is active, the section
// header stays highlighted.
export function DocsSidebarNav({ sections, onNavigate }: DocsSidebarNavProps) {
  const [activeId, setActiveId] = React.useState<string>("")

  React.useEffect(() => {
    const ids = sections.flatMap((s) => [
      s.id,
      ...s.headings.filter((h) => h.level === 2).map((h) => h.id),
    ])

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id)
        })
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    )

    ids.forEach((id) => {
      const el = document.getElementById(id)
      if (el) observer.observe(el)
    })

    return () => observer.disconnect()
  }, [sections])

  return (
    <nav className="flex flex-col gap-6 text-sm">
      {sections.map((section) => {
        const sectionActive = activeId === section.id
        const headingIds = section.headings.filter((h) => h.level === 2).map((h) => h.id)
        const headingActive = headingIds.includes(activeId)
        const isOpen = sectionActive || headingActive

        return (
          <div key={section.id}>
            <a
              href={`#${section.id}`}
              onClick={onNavigate}
              className={cn(
                "mb-2 block rounded-md px-2 py-1 text-xs font-semibold uppercase tracking-wider no-underline transition-colors",
                isOpen ? "text-ink-display" : "text-ink-muted",
              )}
            >
              {section.title}
            </a>
            {section.headings.filter((h) => h.level === 2).length > 0 && (
              <ul className="flex flex-col gap-0.5">
                {section.headings
                  .filter((h) => h.level === 2)
                  .map((heading) => {
                    const isActive = activeId === heading.id
                    return (
                      <li key={heading.id}>
                        <a
                          href={`#${heading.id}`}
                          onClick={onNavigate}
                          className={cn(
                            "block rounded-md px-2 py-1.5 no-underline transition-colors",
                            isActive
                              ? "bg-accent-faded text-accent-display"
                              : "text-ink-body hover:bg-surface hover:text-ink-display",
                          )}
                        >
                          {heading.text}
                        </a>
                      </li>
                    )
                  })}
              </ul>
            )}
          </div>
        )
      })}
    </nav>
  )
}
